import { Money } from '../ui/Money';
import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Banknote, Plus, Trash2, CheckCircle, Clock, AlertCircle, CreditCard, Pencil, Landmark } from 'lucide-react';
import { format } from 'date-fns';
import DeleteConfirmDialog from '../DeleteConfirmDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EOSBPaymentSection = ({ employee, eosbAmount, onUpdate }) => {
  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingInstallment, setEditingInstallment] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [installmentToDelete, setInstallmentToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [installmentForm, setInstallmentForm] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: '',
    notes: ''
  });

  const token = localStorage.getItem('token');
  const userRole = localStorage.getItem('userRole');
  const isViewer = userRole === 'viewer';

  // Only show for resigned/terminated employees
  const isExitedEmployee = ['Resigned', 'Terminated'].includes(employee?.employment_status);

  useEffect(() => {
    if (employee?.id && isExitedEmployee) {
      fetchPaymentData();
    }
  }, [employee?.id]);

  const fetchPaymentData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/eosb-payments/${employee.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPaymentData(data);
      }
    } catch (error) {
      console.error('Error fetching EOSB payment:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkFullyPaid = async () => {
    if (!window.confirm(`Mark EOSB of AED ${eosbAmount?.toLocaleString()} as fully paid?`)) return;

    try {
      const response = await fetch(`${API_URL}/api/eosb-payments/${employee.id}/mark-paid?total_amount=${eosbAmount}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        await fetchPaymentData();
        onUpdate?.();
      }
    } catch (error) {
      console.error('Error marking EOSB paid:', error);
    }
  };

  const handleMoveToVault = async () => {
    const remaining = (eosbAmount || 0) - (paymentData?.paid_amount || 0);
    if (!window.confirm(
      `Move the UNCLAIMED EOSB of AED ${remaining.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} `
      + `for ${employee.name} to the Company Vault?\n\nThis records the amount as NOT paid to the employee and held by the company. `
      + `It will stop appearing as pending and stop the Finance reminders.`
    )) return;
    try {
      const response = await fetch(`${API_URL}/api/eosb-payments/${employee.id}/move-to-vault`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: 'Unclaimed EOSB — moved to Company Vault' }),
      });
      if (response.ok) {
        await fetchPaymentData();
        onUpdate?.();
      } else {
        const err = await response.json().catch(() => ({}));
        alert(err.detail || 'Failed to move EOSB to vault');
      }
    } catch (error) {
      console.error('Error moving EOSB to vault:', error);
    }
  };

  const handleAddInstallment = async () => {
    if (!installmentForm.amount || parseFloat(installmentForm.amount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    try {
      // First ensure payment record exists
      if (!paymentData || paymentData.status === 'pending' && paymentData.total_amount === 0) {
        await fetch(`${API_URL}/api/eosb-payments`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            employee_id: employee.id,
            total_amount: eosbAmount,
            status: 'pending',
            installments: []
          })
        });
      }

      // Add installment
      const response = await fetch(`${API_URL}/api/eosb-payments/${employee.id}/add-installment`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          date: installmentForm.date,
          amount: parseFloat(installmentForm.amount),
          notes: installmentForm.notes
        })
      });

      if (response.ok) {
        await fetchPaymentData();
        setInstallmentForm({
          date: new Date().toISOString().split('T')[0],
          amount: '',
          notes: ''
        });
        setDialogOpen(false);
        onUpdate?.();
      }
    } catch (error) {
      console.error('Error adding installment:', error);
    }
  };

  const handleDeleteInstallment = async (installmentId) => {
    setInstallmentToDelete(installmentId);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteInstallment = async () => {
    if (!installmentToDelete) return;
    setDeleting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/eosb-payments/${employee.id}/installment/${installmentToDelete}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        await fetchPaymentData();
        onUpdate?.();
      }
    } catch (error) {
      console.error('Error deleting installment:', error);
    } finally {
      setDeleting(false);
      setInstallmentToDelete(null);
    }
  };

  const handleEditInstallment = (installment) => {
    setEditingInstallment(installment);
    setInstallmentForm({
      date: installment.date || new Date().toISOString().split('T')[0],
      amount: installment.amount?.toString() || '',
      notes: installment.notes || ''
    });
    setEditDialogOpen(true);
  };

  const handleUpdateInstallment = async () => {
    if (!installmentForm.amount || parseFloat(installmentForm.amount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/eosb-payments/${employee.id}/installment/${editingInstallment.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          date: installmentForm.date,
          amount: parseFloat(installmentForm.amount),
          notes: installmentForm.notes
        })
      });

      if (response.ok) {
        await fetchPaymentData();
        setInstallmentForm({
          date: new Date().toISOString().split('T')[0],
          amount: '',
          notes: ''
        });
        setEditDialogOpen(false);
        setEditingInstallment(null);
        onUpdate?.();
      }
    } catch (error) {
      console.error('Error updating installment:', error);
    }
  };

  if (!isExitedEmployee) return null;
  if (loading) return <div className="animate-pulse h-20 bg-gray-100 rounded-lg"></div>;

  const paidAmount = paymentData?.paid_amount || 0;
  const remainingAmount = (eosbAmount || 0) - paidAmount;
  const installments = paymentData?.installments || [];
  const status = paymentData?.status || 'pending';
  const movedToVault = paymentData?.moved_to_vault || status === 'vault';

  const getStatusBadge = () => {
    if (movedToVault) {
      return <Badge className="bg-indigo-500"><Landmark className="h-3 w-3 mr-1" />In Company Vault</Badge>;
    }
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" />Fully Paid</Badge>;
      case 'partial':
        return <Badge className="bg-amber-500"><Clock className="h-3 w-3 mr-1" />Partially Paid</Badge>;
      default:
        return <Badge className="bg-red-500"><AlertCircle className="h-3 w-3 mr-1" />Pending</Badge>;
    }
  };

  return (
    <div className="border rounded-lg p-4 bg-gradient-to-br from-emerald-50 to-teal-50 mt-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold flex items-center gap-2 text-emerald-700">
          <Banknote className="h-5 w-5" />
          EOSB Payment Status
        </h3>
        {getStatusBadge()}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-white rounded-lg p-3 border">
          <p className="text-xs text-gray-500">Total EOSB</p>
          <p className="text-lg font-bold text-gray-900"><Money value={eosbAmount || 0} /></p>
        </div>
        <div className="bg-white rounded-lg p-3 border">
          <p className="text-xs text-gray-500">Paid Amount</p>
          <p className="text-lg font-bold text-green-600"><Money value={paidAmount} /></p>
        </div>
        <div className="bg-white rounded-lg p-3 border">
          <p className="text-xs text-gray-500">Remaining</p>
          <p className={`text-lg font-bold ${remainingAmount > 0 ? 'text-red-600' : 'text-green-600'}`}>
            <Money value={remainingAmount} />
          </p>
        </div>
      </div>

      {/* Progress bar */}
      {eosbAmount > 0 && (
        <div className="mb-4">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Payment Progress</span>
            <span>{Math.min(100, Math.round((paidAmount / eosbAmount) * 100))}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full ${status === 'paid' ? 'bg-green-500' : 'bg-amber-500'}`}
              style={{ width: `${Math.min(100, (paidAmount / eosbAmount) * 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Installments List */}
      {installments.length > 0 && (
        <div className="mb-4">
          <p className="text-sm font-medium text-gray-700 mb-2 bg-blue-600 text-white px-2 py-1 rounded inline-block">
            Payment Installments ({installments.length})
          </p>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {installments.map((inst, idx) => (
              <div key={inst.id || idx} className="flex items-center justify-between bg-white p-2 rounded border">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-sm">
                    {idx + 1}
                  </div>
                  <div>
                    <p className="font-medium text-sm"><Money value={inst.amount} /></p>
                    <p className="text-xs text-gray-500">
                      {inst.date ? format(new Date(inst.date), 'dd MMM yyyy') : 'N/A'}
                      {inst.notes && ` - ${inst.notes}`}
                    </p>
                  </div>
                </div>
                {!isViewer && (
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditInstallment(inst)}
                      title="Edit installment"
                    >
                      <Pencil className="h-4 w-4 text-blue-500" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteInstallment(inst.id)}
                      title="Delete installment"
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      {!isViewer && status !== 'paid' && !movedToVault && (
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="flex-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                  <Plus className="h-4 w-4 mr-1" />
                  Add Installment
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add EOSB Payment Installment</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Payment Date</Label>
                    <Input
                      type="date"
                      value={installmentForm.date}
                      onChange={(e) => setInstallmentForm(prev => ({ ...prev, date: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Amount (AED)</Label>
                    <Input
                      type="number"
                      value={installmentForm.amount}
                      onChange={(e) => setInstallmentForm(prev => ({ ...prev, amount: e.target.value }))}
                      placeholder={`Remaining: ${remainingAmount.toLocaleString()}`}
                    />
                  </div>
                  <div>
                    <Label>Notes (optional)</Label>
                    <Input
                      value={installmentForm.notes}
                      onChange={(e) => setInstallmentForm(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="e.g., Bank transfer, Cheque #123"
                    />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleAddInstallment} className="bg-emerald-600 hover:bg-emerald-700">
                      Add Installment
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <Button
              onClick={handleMarkFullyPaid}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="h-4 w-4 mr-1" />
              Mark Fully Paid
            </Button>
          </div>

          {remainingAmount > 0 && (
            <Button
              onClick={handleMoveToVault}
              variant="outline"
              className="w-full border-indigo-300 text-indigo-700 hover:bg-indigo-50"
              data-testid="eosb-move-to-vault-btn"
            >
              <Landmark className="h-4 w-4 mr-1" />
              Unclaimed EOSB → Move to Company Vault
            </Button>
          )}
        </div>
      )}

      {movedToVault && (
        <div className="text-center text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg py-3 px-2" data-testid="eosb-vault-note">
          <Landmark className="h-5 w-5 inline mr-2" />
          Unclaimed EOSB of <b><Money value={paymentData?.vault_amount || remainingAmount} digits={2} /></b> moved to the Company Vault.
          <p className="text-xs text-indigo-500 mt-1">Not paid to the employee · no longer pending. Manage it in Staff Vault.</p>
        </div>
      )}

      {status === 'paid' && !movedToVault && (
        <div className="text-center text-green-600 py-2">
          <CheckCircle className="h-5 w-5 inline mr-2" />
          EOSB has been fully paid
        </div>
      )}

      {/* Edit Installment Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Payment Installment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Payment Date</Label>
              <Input
                type="date"
                value={installmentForm.date}
                onChange={(e) => setInstallmentForm(prev => ({ ...prev, date: e.target.value }))}
              />
            </div>
            <div>
              <Label>Amount (AED)</Label>
              <Input
                type="number"
                value={installmentForm.amount}
                onChange={(e) => setInstallmentForm(prev => ({ ...prev, amount: e.target.value }))}
              />
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Input
                value={installmentForm.notes}
                onChange={(e) => setInstallmentForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="e.g., Bank transfer, Cheque #123"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { setEditDialogOpen(false); setEditingInstallment(null); }}>Cancel</Button>
              <Button onClick={handleUpdateInstallment} className="bg-blue-600 hover:bg-blue-700">
                Update Installment
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title="Delete Payment Installment"
        description="Are you sure you want to delete this EOSB payment installment? This will affect the total paid amount."
        onConfirm={confirmDeleteInstallment}
        loading={deleting}
      />
    </div>
  );
};

export default EOSBPaymentSection;
